// react
import React, { useEffect, useMemo } from "react";

// prop-types
import PropTypes from "prop-types";

// react-i18next
import { useTranslation } from "react-i18next";

// @mui
import { Dialog, DialogTitle, DialogContent, DialogActions, Typography, Box, Grid, IconButton, SwipeableDrawer, useMediaQuery, Button, Tooltip, Divider } from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";

// ui-components
import { Puller } from "../Puller";

// utils
import convertTimestamp from "utils/convertTimestamp";

// A ---------------------------------------------------------------------- M

const getButtonStyle = (action) => ({
  py: 0.5,
  borderColor: "divider",
  color: action === "close" ? "error.main" : "primary.main",
  textTransform: "none",
  boxShadow: "none",
  borderRadius: 10,
  "&:hover": {
    borderColor: action === "close" ? "error.dark" : "primary.dark",
    backgroundColor: "action.hover",
  },
});

const DataDialog = ({ data, open, onClose, handleOpenFile, handleDownload }) => {
  console.log(data);

  const { t } = useTranslation();
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));

  const rawFields = data?.data || {};

  const sortedEntries = useMemo(() => {
    const entries = Object.entries(rawFields).filter(([key]) => key !== "storagePath" && key !== "attachment");
    entries.sort(([a], [b]) => a.localeCompare(b));
    return entries;
  }, [rawFields]);

  useEffect(() => {
    const handlePopstate = () => {
      if (open) onClose();
    };
    window.addEventListener("popstate", handlePopstate);
    return () => window.removeEventListener("popstate", handlePopstate);
  }, [open, onClose]);

  const typeMap = {
    info: t("note"),
    doc: t("document"),
    img: t("image"),
    mps: t("mps"),
    CDNFile: t("file"),
    sensor_data_aggregated: t("sensor_data_aggregated"),
    ai_data_out: t("ai_data_out"),
    like_sirti: t("like_sirti"),
  };
  const typeLabel = typeMap[data.type] || t("custom");

  const renderField = (label, value) => {
    if (value == null || value === "") return null;

    const isArray = Array.isArray(value);
    const isObject = typeof value === "object" && !isArray;

    let content;

    if (isObject) {
      content = (
        <Box
          sx={{
            backgroundColor: "grey.300",
            p: 1,
            borderRadius: 1,
            fontFamily: "monospace",
            fontSize: 12,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            overflowX: "auto",
            maxHeight: 200,
          }}
        >
          <pre style={{ margin: 0 }}>{JSON.stringify(value, null, 2)}</pre>
        </Box>
      );
    } else if (isArray) {
      content = (
        <Box
          sx={{
            backgroundColor: "grey.50",
            p: 1,
            borderRadius: 1,
            fontFamily: "monospace",
            fontSize: 12,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            overflowX: "auto",
          }}
        >
          {value.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              [ ] (empty array)
            </Typography>
          ) : (
            value.map((item, i) => (
              <Box key={i} sx={{ mb: 0.5 }}>
                {typeof item === "object" ? (
                  <pre style={{ margin: 0 }}>{JSON.stringify(item, null, 2)}</pre>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    {String(item)}
                  </Typography>
                )}
              </Box>
            ))
          )}
        </Box>
      );
    } else {
      content = (
        <Typography
          variant="body2"
          sx={{
            wordBreak: "break-all",
            color: "text.secondary",
            whiteSpace: "pre-wrap",
          }}
        >
          {String(value)}
        </Typography>
      );
    }

    return (
      <Grid item xs={12} sm={6} key={label}>
        <Typography variant="body2" sx={{ fontWeight: "bold", mb: 0.25, color: "text.primary" }}>
          {label}
        </Typography>
        {content}
      </Grid>
    );
  };

  const DetailsForm = (
    <Grid container spacing={3}>
      <Grid item container spacing={2}>
        {renderField(t("record_id"), data.id)}
        {renderField(t("databox"), data.databoxName)}
        {renderField(t("generation_datetime"), data.timestamp ? convertTimestamp(data.timestamp) : "N/A")}
        {renderField(t("creator"), data.creator_email || data.creator_uuid || "N/A")}
        {renderField(t("type"), typeLabel)}
        {renderField(t("visibility"), data.public ? t("public") : t("private"))}
      </Grid>

      <Grid item xs={12}>
        <Divider sx={{ borderColor: "divider" }} />
      </Grid>

      <Grid item xs={12}>
        <Box
          sx={{
            backgroundColor: "grey.100",
            borderRadius: 1,
            p: 2,
            maxHeight: "300px",
            overflowY: "auto",
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1, color: "text.primary" }}>
            {t("Record Data")}
          </Typography>

          <Divider sx={{ mb: 2, borderColor: "divider" }} />

          <Grid container spacing={2}>
            {(data.data?.storagePath || data.data?.attachment) && (
              <>
                <Grid item xs={6}>
                  <Button variant="outlined" fullWidth sx={getButtonStyle("open")} onClick={() => handleOpenFile(data)}>
                    {t("open_file")}
                  </Button>
                </Grid>

                <Grid item xs={6}>
                  <Button variant="outlined" fullWidth sx={getButtonStyle("open")} onClick={() => handleDownload(data)}>
                    {t("download_file")}
                  </Button>
                </Grid>
              </>
            )}

            {sortedEntries.map(([key, value]) => renderField(key, value))}
          </Grid>
        </Box>
      </Grid>
    </Grid>
  );

  return isMobile ? (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={() => {}}
      sx={{
        "& .MuiDrawer-paper": {
          width: "100%",
          height: "90%",
          borderTopLeftRadius: "4%",
          borderTopRightRadius: "4%",
        },
      }}
    >
      <Puller />
      <Box sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight="bold" textAlign="center" mb={3}>
          {t("record_details")}
        </Typography>
        {DetailsForm}
      </Box>
    </SwipeableDrawer>
  ) : (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      sx={{
        "& .MuiPaper-root": {
          borderRadius: 2,
          p: 2,
          boxShadow: "0px 2px 5px rgba(0, 0, 0, 0.2)",
        },
      }}
    >
      <DialogTitle>
        <Grid container alignItems="center" justifyContent="space-between">
          <Grid item>
            <Typography variant="h6" fontWeight="bold" color="text.primary">
              {t("record_details")}
            </Typography>
          </Grid>
          <Grid item>
            <Tooltip title={t("close")} placement="top">
              <IconButton onClick={onClose} color="error" edge="end">
                <CloseOutlinedIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </DialogTitle>
      <DialogContent>{DetailsForm}</DialogContent>
      <DialogActions>
        <Button variant="outlined" sx={getButtonStyle("close")} onClick={onClose}>
          {t("close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

DataDialog.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.string.isRequired,
    databoxName: PropTypes.string,
    timestamp: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    creator_email: PropTypes.string,
    creator_uuid: PropTypes.string,
    type: PropTypes.string.isRequired,
    public: PropTypes.bool,
    data: PropTypes.object,
  }).isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  handleOpenFile: PropTypes.func,
  handleDownload: PropTypes.func,
};

export default DataDialog;
